import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, User as UserIcon, LogOut, LayoutDashboard } from "lucide-react";
import logo from "@/assets/logo.png";
import { useAuth } from "@/contexts/AuthContext";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Studios", to: "/studios" },
  { label: "Services", to: "/services" },
  { label: "Photography", to: "/columbus-photography-service" },
  { label: "Production", to: "/production-service" },
  { label: "Post Production", to: "/post-production" },
  { label: "Gallery", to: "/gallery" },
  { label: "Blog", to: "/blog" },
  { label: "Shop", to: "/shop" },
  { label: "Contact", to: "/contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, isAdmin, signOut } = useAuth();
  const displayName =
    (user?.user_metadata?.full_name as string | undefined) ||
    (user?.user_metadata?.name as string | undefined) ||
    user?.email?.split("@")[0] ||
    "";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <>
      {/* Top utility bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-background/95 border-b border-border text-xs">
        <div className="container mx-auto px-4 lg:px-8 flex items-center justify-end gap-4 h-8">
          {user ? (
            <>
              <Link to="/account" className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors font-body">
                <UserIcon className="w-3 h-3" />
                {displayName}
              </Link>
              {isAdmin && (
                <>
                  <span className="text-muted-foreground">|</span>
                  <Link to="/admin" className="text-primary hover:text-primary/80 transition-colors font-body">Admin</Link>
                </>
              )}
              <span className="text-muted-foreground">|</span>
              <button onClick={signOut} className="text-muted-foreground hover:text-foreground transition-colors font-body">Sign Out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-muted-foreground hover:text-foreground transition-colors font-body">Login</Link>
              <span className="text-muted-foreground">|</span>
              <Link to="/signup" className="text-muted-foreground hover:text-foreground transition-colors font-body">Register</Link>
            </>
          )}
          <Link to="/book" className="bg-primary text-primary-foreground px-3 py-0.5 font-display tracking-[0.15em] text-[10px] hover:bg-primary/90 transition-colors">
            RENT STUDIO
          </Link>
        </div>
      </div>

      {/* Main navbar */}
      <nav
        className={`fixed top-8 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-surface/95 backdrop-blur-md border-b border-border" : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={logo} alt="MyStudio" className="h-8 lg:h-10" />
          </Link>

          {/* Desktop Right: phone, email, rent studio, hamburger */}
          <div className="hidden lg:flex items-center gap-6">
            <a href="tel:7064787640" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors font-body">
              <Phone className="w-4 h-4" />
              706.478.7640
            </a>
            <a href="mailto:info@bookmystudiospace.com" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors font-body">
              <Mail className="w-4 h-4" />
              info@bookmystudiospace.com
            </a>
            <Link to="/book" className="bg-primary text-primary-foreground px-5 py-2 font-display tracking-[0.15em] text-xs hover:bg-primary/90 transition-colors">
              RENT STUDIO
            </Link>
          </div>

          {/* Hamburger Menu Toggle (all sizes) */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-foreground z-50"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Full-screen menu overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-background/98 backdrop-blur-md flex flex-col items-center justify-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-2xl font-display tracking-widest uppercase transition-colors hover:text-primary ${
                location.pathname === link.to ? "text-primary" : "text-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="flex flex-col items-center gap-3 mt-6">
            <Link to="/book" className="bg-primary text-primary-foreground px-8 py-3 font-display tracking-[0.15em] text-sm hover:bg-primary/90 transition-colors">
              RENT STUDIO
            </Link>
            {user ? (
              <div className="flex flex-col items-center gap-2">
                <Link to="/account" className="flex items-center gap-2 font-body text-foreground hover:text-primary">
                  <UserIcon className="w-4 h-4" /> {displayName}
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="flex items-center gap-2 font-body text-primary">
                    <LayoutDashboard className="w-4 h-4" /> Admin
                  </Link>
                )}
                <button onClick={signOut} className="flex items-center gap-2 font-body text-muted-foreground hover:text-foreground">
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4 font-body">
                <Link to="/login" className="text-foreground hover:text-primary">Login</Link>
                <span className="text-muted-foreground">|</span>
                <Link to="/signup" className="text-foreground hover:text-primary">Register</Link>
              </div>
            )}
            <a href="tel:7064787640" className="text-muted-foreground mt-2 font-body">706.478.7640</a>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
